import { useState, useEffect, useMemo } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import { usePermissions } from '../hooks/usePermissions';
import { MinStockWithDetails, Location, MetalPiece } from '../types';
import { SkeletonTable } from './Skeleton';

interface AutoRecalcSummary {
  updated: number;
  created: number;
  summaries: Array<{
    location_code: string;
    location_name: string;
    entries: number;
    total_min_qty: number;
  }>;
}

interface PieceWithMinStock {
  piece: MetalPiece;
  minStockByLocation: Map<number, MinStockWithDetails>; // location_id -> MinStockWithDetails
}

export default function MinStockMetal() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();
  const { canEdit } = usePermissions();
  const [allPieces, setAllPieces] = useState<MetalPiece[]>([]);
  const [minStock, setMinStock] = useState<MinStockWithDetails[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [selectedPieceTypes, setSelectedPieceTypes] = useState<Set<string>>(new Set(['ANILLO', 'COLGANTE', 'PULSERA', 'REASA']));
  const [configEditing, setConfigEditing] = useState<Map<string, { min_qty_base: string; location_id: number }>>(new Map()); // key: "pieceId-locationId"
  const [sortAscending, setSortAscending] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [autoSummary, setAutoSummary] = useState<AutoRecalcSummary | null>(null);
  const [multiplier, setMultiplier] = useState<number>(1);

  useEffect(() => {
    loadData();
    loadMultiplier();
    // Cargar filtros guardados
    const savedFilters = localStorage.getItem('min_stock_metal_filter_types');
    if (savedFilters) {
      try {
        setSelectedPieceTypes(new Set(JSON.parse(savedFilters)));
      } catch (e) {
        console.error('Error al cargar filtros guardados:', e);
      }
    }
  }, []);

  const loadMultiplier = async () => {
    try {
      const response = await api.get('/settings/min-stock-multiplier');
      setMultiplier(response.data.multiplier || 1);
    } catch (err: any) {
      console.error('Error al cargar multiplicador:', err);
      setMultiplier(1);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [piecesRes, minStockRes, locationsRes] = await Promise.all([
        api.get('/metal-pieces'),
        api.get('/min-stock'),
        api.get('/locations'),
      ]);

      // Filtrar solo piezas activas
      const activePieces = Array.isArray(piecesRes.data) 
        ? piecesRes.data.filter((p: MetalPiece) => p.is_active !== false)
        : (piecesRes.data?.data || []).filter((p: MetalPiece) => p.is_active !== false);
      
      setAllPieces(activePieces);

      // Filtrar solo METAL
      const metalMinStock = minStockRes.data.filter((ms: MinStockWithDetails) => ms.item_type === 'METAL');
      setMinStock(metalMinStock);
      setLocations(locationsRes.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Función helper para determinar el tipo de pieza basándose en el código
  const getPieceType = (codigo: string): 'ANILLO' | 'COLGANTE' | 'PULSERA' | 'REASA' | null => {
    if (!codigo || typeof codigo !== 'string') return null;
    
    const cleaned = codigo.trim().toUpperCase();
    if (cleaned.length === 0) return null;
    
    const firstChar = cleaned.charAt(0);
    
    if (firstChar === 'A') return 'ANILLO';
    if (firstChar === 'C') return 'COLGANTE';
    if (firstChar === 'P') return 'PULSERA';
    if (firstChar === 'R') return 'REASA';
    
    return null;
  };

  const togglePieceType = (type: string) => {
    const newTypes = new Set(selectedPieceTypes);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    setSelectedPieceTypes(newTypes);
    localStorage.setItem('min_stock_metal_filter_types', JSON.stringify(Array.from(newTypes)));
  };


  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este stock mínimo?')) {
      return;
    }

    try {
      await api.delete(`/min-stock/${id}`);
      showSuccess('Stock mínimo eliminado correctamente');
      loadData();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar stock mínimo');
    }
  };

  const handleAutoRefresh = async () => {
    try {
      setRecalculating(true);
      const response = await api.post('/min-stock/recalculate');
      const { message, ...summary } = response.data as AutoRecalcSummary & { message?: string };
      setAutoSummary(summary);
      showSuccess(message || 'Stock mínimos recalculados automáticamente');
      await loadData();
      await loadMultiplier();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al recalcular stock mínimos');
    } finally {
      setRecalculating(false);
    }
  };

  const sortListByCode = <T,>(list: T[], getter: (item: T) => string) => {
    const direction = sortAscending ? 1 : -1;
    return [...list].sort((a, b) => {
      const codeA = getter(a)?.toUpperCase() || '';
      const codeB = getter(b)?.toUpperCase() || '';
      if (codeA === codeB) return 0;
      return codeA > codeB ? direction : -direction;
    });
  };

  // Combinar piezas con sus mínimos por ubicación
  const piecesWithMinStock = useMemo(() => {
    const result: PieceWithMinStock[] = [];
    const minStockMap = new Map<number, Map<number, MinStockWithDetails>>(); // piece_id -> location_id -> MinStock

    // Organizar mínimos por pieza y ubicación
    minStock.forEach((ms) => {
      if (!ms.metal_piece) return;
      const pieceId = ms.metal_piece.id;
      if (!minStockMap.has(pieceId)) {
        minStockMap.set(pieceId, new Map());
      }
      const locationMap = minStockMap.get(pieceId)!;
      if (ms.location_id) {
        locationMap.set(ms.location_id, ms);
      }
    });

    // Crear estructura combinada
    allPieces.forEach((piece) => {
      const locationMap = minStockMap.get(piece.id) || new Map();
      result.push({
        piece,
        minStockByLocation: locationMap,
      });
    });

    return result;
  }, [allPieces, minStock]);

  // Filtrar y ordenar piezas
  const filteredAndSortedPieces = useMemo(() => {
    let filtered = piecesWithMinStock;

    // Filtrar por tipo de pieza
    if (selectedPieceTypes.size > 0 && selectedPieceTypes.size < 4) {
      filtered = filtered.filter((item) => {
        const pieceType = getPieceType(item.piece.cod_pieza);
        return pieceType && selectedPieceTypes.has(pieceType);
      });
    }

    // Ordenar por código
    return sortListByCode(filtered, (item) => item.piece.cod_pieza);
  }, [piecesWithMinStock, selectedPieceTypes, sortAscending]);

  // Obtener ubicaciones relevantes (talleres)
  const relevantLocations = useMemo(() => {
    return locations.filter((loc) => 
      loc.code === 'TALLER_PLATA' || loc.code === 'TALLER_LATON' ||
      loc.code === 'ACABADO_PLATA' || loc.code === 'ACABADO_PVD' || loc.code === 'ACABADO_DLC'
    );
  }, [locations]);

  const handleConfigEdit = (pieceId: number, locationId: number, currentMin: number | null) => {
    const key = `${pieceId}-${locationId}`;
    const newEditing = new Map(configEditing);
    newEditing.set(key, {
      min_qty_base: currentMin !== null ? currentMin.toString() : '0',
      location_id: locationId,
    });
    setConfigEditing(newEditing);
  };

  const handleConfigFieldChange = (
    pieceId: number,
    locationId: number,
    value: string
  ) => {
    const key = `${pieceId}-${locationId}`;
    const newEditing = new Map(configEditing);
    const current = newEditing.get(key) || { min_qty_base: '0', location_id: locationId };
    current.min_qty_base = value;
    newEditing.set(key, current);
    setConfigEditing(newEditing);
  };

  const handleConfigCancel = (pieceId: number, locationId: number) => {
    const key = `${pieceId}-${locationId}`;
    const newEditing = new Map(configEditing);
    newEditing.delete(key);
    setConfigEditing(newEditing);
  };

  const handleConfigSave = async (pieceId: number, locationId: number) => {
    const key = `${pieceId}-${locationId}`;
    const editing = configEditing.get(key);
    if (!editing) return;

    const min_qty_base = parseInt(editing.min_qty_base, 10);

    if (isNaN(min_qty_base) || min_qty_base < 0) {
      showError('El stock mínimo base debe ser un número válido mayor o igual a 0');
      return;
    }

    try {
      // Buscar si ya existe un mínimo para esta pieza y ubicación
      const existingMinStock = minStock.find(
        (ms) => ms.item_id === pieceId && ms.location_id === locationId
      );

      if (existingMinStock) {
        // Actualizar existente
        const data: any = {
          min_qty_base,
          location_id: locationId,
        };
        await api.put(`/min-stock/${existingMinStock.id}`, data);
        showSuccess('Stock mínimo actualizado correctamente');
      } else {
        // Crear nuevo
        const data: any = {
          item_type: 'METAL',
          item_id: pieceId,
          location_id: locationId,
          min_qty_base,
        };
        await api.post('/min-stock', data);
        showSuccess('Stock mínimo creado correctamente');
      }

      const newEditing = new Map(configEditing);
      newEditing.delete(key);
      setConfigEditing(newEditing);

      await loadData();
      await loadMultiplier();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al guardar stock mínimo');
    }
  };


  if (loading) {
    return (
      <div className="tab-content">
        <SkeletonTable rows={8} columns={6} />
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="section-header" style={{ background: '#ffffff' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ color: 'var(--text-primary)' }}>Establecer mínimos - Piezas</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.5rem', marginBottom: 0 }}>
            Gestiona los stocks mínimos configurados para piezas metálicas. Puedes editar mínimos directamente desde la sección <strong>Stock</strong> cuando hay stock disponible.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        {canEdit('min-stock-metal') && (
          <button
            className="btn btn-primary"
            onClick={handleAutoRefresh}
            disabled={recalculating}
          >
            {recalculating ? 'Recalculando...' : '🔄 Recalcular Mínimos Automáticamente'}
          </button>
        )}
      </div>

      {autoSummary && (
        <div className="table-container" style={{ marginBottom: '1rem', padding: '1rem' }}>
          <div className="flex-between" style={{ marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <strong>Último recálculo automático</strong>
            <span>
              Actualizados: {autoSummary.updated} · Creados: {autoSummary.created}
            </span>
          </div>
          {autoSummary.summaries.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              No se modificaron ubicaciones gestionadas.
            </p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Ubicación</th>
                  <th>Entradas</th>
                  <th>Stock Mínimo Total</th>
                </tr>
              </thead>
              <tbody>
                {autoSummary.summaries.map((summary) => (
                  <tr key={summary.location_code}>
                    <td>
                      <strong>{summary.location_name}</strong>
                      <br />
                      <small style={{ color: 'var(--text-secondary)' }}>{summary.location_code}</small>
                    </td>
                    <td>{summary.entries}</td>
                    <td>{summary.total_min_qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}


      <div className="table-container" style={{ marginBottom: '0.5rem', padding: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1, minWidth: '300px' }}>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.125rem' }}>
              Filtrar por tipo de pieza:
            </label>
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              {[
                { value: 'ANILLO', label: 'Anillo', color: '#3b82f6' },
                { value: 'COLGANTE', label: 'Colgante', color: '#a855f7' },
                { value: 'PULSERA', label: 'Pulsera', color: '#10b981' },
                { value: 'REASA', label: 'Traza', color: '#f59e0b' },
              ].map((type) => {
                const isSelected = selectedPieceTypes.has(type.value);
                return (
                  <div
                    key={type.value}
                    onClick={() => togglePieceType(type.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.4rem 0.75rem',
                      borderRadius: '20px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--border)'}`,
                      backgroundColor: isSelected ? `${type.color}15` : 'transparent',
                      color: isSelected ? type.color : 'var(--text-secondary)',
                      cursor: 'pointer',
                      fontWeight: isSelected ? 600 : 400,
                      transition: 'all 0.2s ease',
                      fontSize: '0.85rem',
                      userSelect: 'none',
                      boxShadow: isSelected ? `0 2px 4px ${type.color}20` : 'none',
                    }}
                  >
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '3px',
                      border: `1.5px solid ${isSelected ? type.color : 'var(--text-secondary)'}`,
                      backgroundColor: isSelected ? type.color : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                    }}>
                      {isSelected && (
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      )}
                    </div>
                    <span>{type.label}</span>
                    {isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePieceType(type.value);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          padding: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: type.color,
                          cursor: 'pointer',
                          borderRadius: '50%',
                          marginLeft: '2px',
                          opacity: 0.7,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Ubicación:</label>
              <select
                className="input"
                value={filterLocation}
                onChange={(e) => setFilterLocation(e.target.value)}
                style={{ width: '200px' }}
              >
                <option value="">Todas las ubicaciones</option>
                {relevantLocations.map((loc) => (
                  <option key={loc.id} value={loc.id.toString()}>
                    {loc.nombre}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setSortAscending((prev) => !prev)}
              style={{ whiteSpace: 'nowrap' }}
            >
              Orden: {sortAscending ? 'A → Z' : 'Z → A'}
            </button>
          </div>
        </div>
      </div>

      <div>
        <h3 style={{ marginBottom: '0.5rem' }}>Piezas Activas - Stocks Mínimos</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Haz clic en los valores de "Mínimo Base" para editar directamente. Puedes crear nuevos mínimos haciendo clic en "Crear" cuando no existe uno configurado.
        </p>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Ubicación</th>
                <th style={{ textAlign: 'center' }}>Mínimo Base<br/><small>(sin multiplicar)</small></th>
                <th style={{ textAlign: 'center' }}>Mínimo Efectivo<br/><small>(×{multiplier})</small></th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedPieces.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    No hay piezas activas con los filtros seleccionados
                  </td>
                </tr>
              ) : (
                filteredAndSortedPieces.flatMap((item) => {
                  const piece = item.piece;
                  const pieceType = getPieceType(piece.cod_pieza);
                  
                  // Si hay filtro de ubicación, mostrar solo esa ubicación
                  if (filterLocation) {
                    const locationId = parseInt(filterLocation, 10);
                    const minStockItem = item.minStockByLocation.get(locationId);
                    const location = locations.find(l => l.id === locationId);
                    if (!location) return [];

                    const key = `${piece.id}-${locationId}`;
                    const isConfigEditing = configEditing.has(key);
                    const configValues = configEditing.get(key);
                    const currentMin = minStockItem?.min_qty_base ?? null;

                    return [(
                      <tr key={key}>
                        <td>
                          <strong
                            style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              backgroundColor: piece.material_base === 'LATON' ? '#fde047' : '#9ca3af',
                              color: piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                            }}
                          >
                            {piece.cod_pieza}
                          </strong>
                        </td>
                        <td>{piece.nombre_pieza}</td>
                        <td>
                          {pieceType ? (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              backgroundColor: pieceType === 'ANILLO' ? '#3b82f615' : 
                                             pieceType === 'COLGANTE' ? '#a855f715' : 
                                             pieceType === 'PULSERA' ? '#10b98115' : '#f59e0b15',
                              color: pieceType === 'ANILLO' ? '#3b82f6' : 
                                     pieceType === 'COLGANTE' ? '#a855f7' : 
                                     pieceType === 'PULSERA' ? '#10b981' : '#f59e0b',
                            }}>
                              {pieceType === 'ANILLO' ? 'Anillo' : 
                               pieceType === 'COLGANTE' ? 'Colgante' : 
                               pieceType === 'PULSERA' ? 'Pulsera' : 'Traza'}
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          {location.nombre}
                          <br />
                          <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                            {location.tipo}
                          </small>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {isConfigEditing ? (
                            <input
                              type="number"
                              min="0"
                              className="input"
                              value={configValues?.min_qty_base ?? (currentMin !== null ? currentMin.toString() : '0')}
                              onChange={(e) => handleConfigFieldChange(piece.id, locationId, e.target.value)}
                              style={{ width: '100px', textAlign: 'center' }}
                              autoFocus
                            />
                          ) : (
                            <span
                              style={{
                                cursor: 'pointer',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                display: 'inline-block',
                                minWidth: '60px',
                                color: currentMin === null ? 'var(--text-secondary)' : 'var(--text-primary)',
                              }}
                              onClick={() => handleConfigEdit(piece.id, locationId, currentMin)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              {currentMin !== null ? currentMin : '-'}
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <strong style={{ color: 'var(--primary)' }}>
                            {currentMin !== null && currentMin > 0 ? Math.round(currentMin * multiplier) : '-'}
                          </strong>
                        </td>
                        <td>
                          {isConfigEditing ? (
                            <div className="flex gap-1">
                              <button
                                className="btn btn-primary"
                                onClick={() => handleConfigSave(piece.id, locationId)}
                                style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                              >
                                Guardar
                              </button>
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleConfigCancel(piece.id, locationId)}
                                style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                              >
                                Cancelar
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-1">
                              <button
                                className="btn btn-secondary"
                                onClick={() => handleConfigEdit(piece.id, locationId, currentMin)}
                                style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                              >
                                {currentMin !== null ? 'Editar' : 'Crear'}
                              </button>
                              {currentMin !== null && canEdit('min-stock-metal') && (
                                <button
                                  className="btn btn-danger"
                                  onClick={() => {
                                    const minStockItem = item.minStockByLocation.get(locationId);
                                    if (minStockItem) {
                                      handleDelete(minStockItem.id);
                                    }
                                  }}
                                  style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                >
                                  Eliminar
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )];
                  } else {
                    // Sin filtro de ubicación: mostrar solo las ubicaciones donde hay mínimo configurado
                    // Si no hay ningún mínimo, mostrar la ubicación de taller correspondiente al material
                    const locationsToShow: Location[] = [];
                    
                    if (item.minStockByLocation.size > 0) {
                      // Mostrar ubicaciones donde hay mínimo configurado
                      item.minStockByLocation.forEach((_, locationId) => {
                        const loc = locations.find(l => l.id === locationId);
                        if (loc) locationsToShow.push(loc);
                      });
                    } else {
                      // Si no hay mínimo configurado, mostrar la ubicación de taller correspondiente
                      const tallerLocation = locations.find(l => 
                        (piece.material_base === 'PLATA' && l.code === 'TALLER_PLATA') ||
                        (piece.material_base === 'LATON' && l.code === 'TALLER_LATON')
                      );
                      if (tallerLocation) {
                        locationsToShow.push(tallerLocation);
                      }
                    }

                    return locationsToShow.map((location) => {
                      const minStockItem = item.minStockByLocation.get(location.id);
                      const key = `${piece.id}-${location.id}`;
                      const isConfigEditing = configEditing.has(key);
                      const configValues = configEditing.get(key);
                      const currentMin = minStockItem?.min_qty_base ?? null;

                      return (
                        <tr key={key}>
                          <td>
                            <strong
                              style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                backgroundColor: piece.material_base === 'LATON' ? '#fde047' : '#d1d5db',
                                color: piece.material_base === 'LATON' ? '#78350f' : '#1f2937',
                              }}
                            >
                              {piece.cod_pieza}
                            </strong>
                          </td>
                          <td>{piece.nombre_pieza}</td>
                          <td>
                            {pieceType ? (
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                backgroundColor: pieceType === 'ANILLO' ? '#3b82f615' : 
                                               pieceType === 'COLGANTE' ? '#a855f715' : 
                                               pieceType === 'PULSERA' ? '#10b98115' : '#f59e0b15',
                                color: pieceType === 'ANILLO' ? '#3b82f6' : 
                                       pieceType === 'COLGANTE' ? '#a855f7' : 
                                       pieceType === 'PULSERA' ? '#10b981' : '#f59e0b',
                              }}>
                                {pieceType === 'ANILLO' ? 'Anillo' : 
                                 pieceType === 'COLGANTE' ? 'Colgante' : 
                                 pieceType === 'PULSERA' ? 'Pulsera' : 'Traza'}
                              </span>
                            ) : '-'}
                          </td>
                          <td>
                            {location.nombre}
                            <br />
                            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                              {location.tipo}
                            </small>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {isConfigEditing ? (
                              <input
                                type="number"
                                min="0"
                                className="input"
                                value={configValues?.min_qty_base ?? (currentMin !== null ? currentMin.toString() : '0')}
                                onChange={(e) => handleConfigFieldChange(piece.id, location.id, e.target.value)}
                                style={{ width: '100px', textAlign: 'center' }}
                                autoFocus
                              />
                            ) : (
                              <span
                                style={{
                                  cursor: 'pointer',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  display: 'inline-block',
                                  minWidth: '60px',
                                  color: currentMin === null ? 'var(--text-secondary)' : 'var(--text-primary)',
                                }}
                                onClick={() => handleConfigEdit(piece.id, location.id, currentMin)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                {currentMin !== null ? currentMin : '-'}
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <strong style={{ color: 'var(--primary)' }}>
                              {currentMin !== null && currentMin > 0 ? Math.round(currentMin * multiplier) : '-'}
                            </strong>
                          </td>
                          <td>
                            {isConfigEditing ? (
                              <div className="flex gap-1">
                                <button
                                  className="btn btn-primary"
                                  onClick={() => handleConfigSave(piece.id, location.id)}
                                  style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                >
                                  Guardar
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => handleConfigCancel(piece.id, location.id)}
                                  style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <button
                                  className="btn btn-secondary"
                                  onClick={() => handleConfigEdit(piece.id, location.id, currentMin)}
                                  style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                >
                                  {currentMin !== null ? 'Editar' : 'Crear'}
                                </button>
                                {currentMin !== null && canEdit('min-stock-metal') && (
                                  <button
                                    className="btn btn-danger"
                                    onClick={() => {
                                      const minStockItem = item.minStockByLocation.get(location.id);
                                      if (minStockItem) {
                                        handleDelete(minStockItem.id);
                                      }
                                    }}
                                    style={{ padding: '0.5rem', fontSize: '0.875rem' }}
                                  >
                                    Eliminar
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  }
                })
              )}
            </tbody>
          </table>
        </div>
        
        <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            <strong>Total piezas activas:</strong> {filteredAndSortedPieces.length} de {allPieces.length} piezas
            {filterLocation && (
              <> · <strong>Mínimos configurados:</strong> {minStock.filter(ms => ms.location_id?.toString() === filterLocation).length}</>
            )}
          </p>
        </div>
      </div>

    </div>
  );
}

